import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Textarea } from '../../components/ui/textarea';
import { RefreshCw, Download, FileText, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { compensationAPI } from '../../lib/api';
import { fmt, statementStatusBadge } from './_shared';

function SendEmailDialog({ open, statement, onClose, onSent }) {
  const [to, setTo] = useState('');
  const [cc, setCc] = useState('');
  const [note, setNote] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (open) { setTo(''); setCc(''); setNote(''); }
  }, [open]);

  const submit = async () => {
    if (!statement) return;
    setSending(true);
    try {
      const cc_emails = cc.split(',').map((e) => e.trim()).filter(Boolean);
      const r = await compensationAPI.emailPayoutStatement(statement.statement_id, {
        to_email: to.trim() || null,
        cc_emails: cc_emails.length ? cc_emails : null,
        note: note.trim() || null,
      });
      if (r.data.sent) {
        toast.success(`Statement emailed to ${r.data.to_email}`);
      } else {
        // Per spec: failures DO NOT block workflow — surface a warning.
        toast.warning(`Email not sent: ${r.data.reason || 'unknown error'}`);
      }
      onSent?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Email request failed');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="stmt-email-dialog">
        <DialogHeader>
          <DialogTitle>Send Statement Email</DialogTitle>
          <DialogDescription>
            Manually email a PDF copy of this statement to the payee. The payment
            workflow is <strong>not</strong> blocked by email failures — they are
            logged separately.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="text-sm bg-zinc-50 p-3 rounded border">
            <div><span className="text-zinc-500">Statement:</span> <span className="font-mono text-xs">{statement?.statement_id}</span></div>
            <div><span className="text-zinc-500">Payee:</span> <strong>{statement?.payee_type}</strong> — {statement?.payee_name || statement?.payee_id}</div>
            <div><span className="text-zinc-500">Final Total:</span> <strong>{fmt(statement?.final_total)}</strong></div>
            {statement?.last_email_at && (
              <div className="mt-2 text-xs text-zinc-600">
                Last sent {statement.last_email_at.slice(0, 19)} to {statement.last_email_to} — {statement.last_email_sent ? '✓ delivered' : '✗ failed'}
              </div>
            )}
          </div>
          <div>
            <Label>To (leave blank to use payee's on-file email)</Label>
            <Input value={to} onChange={(e) => setTo(e.target.value)} placeholder="payee@example.com" data-testid="stmt-email-to" />
          </div>
          <div>
            <Label>CC (comma separated, optional)</Label>
            <Input value={cc} onChange={(e) => setCc(e.target.value)} placeholder="accounting@..., manager@..." data-testid="stmt-email-cc" />
          </div>
          <div>
            <Label>Note (optional — shown above the statement summary)</Label>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} data-testid="stmt-email-note" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={sending} data-testid="stmt-email-confirm">
            <Mail className="h-4 w-4 mr-1" />{sending ? 'Sending…' : 'Send Email'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function StatementsTab() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [genOpen, setGenOpen] = useState(false);
  const [cycle, setCycle] = useState(null);
  const [genPayeeType, setGenPayeeType] = useState('Agency');
  const [generating, setGenerating] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [emailTarget, setEmailTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterStatus !== 'all') params.status = filterStatus;
      const r = await compensationAPI.listPayoutStatements(params);
      setList(r.data.statements || []);
    } catch {
      toast.error('Failed to load statements');
    } finally {
      setLoading(false);
    }
  }, [filterStatus]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { compensationAPI.getCycle(0).then((r) => setCycle(r.data)).catch(() => {}); }, []);

  const generate = async () => {
    if (!cycle) return;
    setGenerating(true);
    try {
      const r = await compensationAPI.generatePayoutStatements({
        cycle_start: cycle.cycle_start, cycle_end: cycle.cycle_end, payee_type: genPayeeType,
      });
      const n = r.data.created_count;
      if (n === 0) {
        toast.warning(`No ${genPayeeType} statements generated — no eligible records in this cycle (missing payee identifiers or all already Paid).`);
      } else {
        toast.success(`Generated ${n} ${genPayeeType} statement${n === 1 ? '' : 's'}`);
      }
      setGenOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Generation failed');
    } finally {
      setGenerating(false);
    }
  };

  const approve = async (id) => {
    try {
      await compensationAPI.approvePayoutStatement(id);
      toast.success('Statement approved');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Approve failed');
    }
  };

  const exportFile = async (id, format) => {
    try {
      const r = await compensationAPI.exportPayoutStatement(id, format);
      const blob = new Blob([r.data], { type: format === 'pdf' ? 'application/pdf' : 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `statement_${id}.${format}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Export failed');
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="p-4 flex items-center gap-3">
          <Button onClick={() => setGenOpen(true)} data-testid="stmt-generate-btn"><FileText className="h-4 w-4 mr-1" />Generate from current cycle</Button>
          <div className="flex items-center gap-2 ml-auto">
            <Label className="text-xs">Status:</Label>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-40" data-testid="stmt-filter-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                {['Draft', 'Approved', 'Scheduled', 'Paid'].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button size="sm" variant="ghost" onClick={load} disabled={loading}>
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Statement</TableHead>
              <TableHead>Cycle</TableHead>
              <TableHead>Payee</TableHead>
              <TableHead className="text-right">Subtotal</TableHead>
              <TableHead className="text-right">Adjustments</TableHead>
              <TableHead className="text-right">Final</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {list.length === 0 && <TableRow><TableCell colSpan={9} className="text-center text-zinc-500 py-8">No statements yet — click Generate above.</TableCell></TableRow>}
            {list.map((s) => (
              <TableRow key={s.statement_id} data-testid={`stmt-row-${s.statement_id}`}>
                <TableCell className="text-xs font-mono">{s.statement_id}</TableCell>
                <TableCell className="text-sm">{(s.cycle_start || '').slice(0, 10)} → {(s.cycle_end || '').slice(0, 10)}</TableCell>
                <TableCell className="text-sm"><span className="font-medium">{s.payee_type}</span><br /><span className="text-xs text-zinc-500">{s.payee_name || s.payee_id}</span></TableCell>
                <TableCell className="text-right">{fmt(s.subtotal)}</TableCell>
                <TableCell className={`text-right ${(s.adjustments_total || 0) < 0 ? 'text-red-600' : ''}`}>{fmt(s.adjustments_total)}</TableCell>
                <TableCell className="text-right font-semibold">{fmt(s.final_total)}</TableCell>
                <TableCell>{statementStatusBadge(s.status)}</TableCell>
                <TableCell className="text-xs">
                  {s.last_email_at ? (
                    <span className={s.last_email_sent ? 'text-emerald-700' : 'text-red-600'}>
                      {s.last_email_sent ? '✓ Sent' : '✗ Failed'}<br />
                      <span className="text-zinc-500">{s.last_email_at.slice(0, 10)}</span>
                    </span>
                  ) : <span className="text-zinc-400">Not sent</span>}
                </TableCell>
                <TableCell className="flex flex-wrap gap-1">
                  {s.status === 'Draft' && <Button size="sm" onClick={() => approve(s.statement_id)} data-testid={`stmt-approve-${s.statement_id}`}>Approve</Button>}
                  <Button size="sm" variant="outline" onClick={() => exportFile(s.statement_id, 'csv')} data-testid={`stmt-csv-${s.statement_id}`}><Download className="h-3 w-3" />CSV</Button>
                  <Button size="sm" variant="outline" onClick={() => exportFile(s.statement_id, 'pdf')} data-testid={`stmt-pdf-${s.statement_id}`}><Download className="h-3 w-3" />PDF</Button>
                  <Button size="sm" variant="outline" onClick={() => setEmailTarget(s)} data-testid={`stmt-email-${s.statement_id}`}><Mail className="h-3 w-3" />Email</Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={genOpen} onOpenChange={setGenOpen}>
        <DialogContent data-testid="stmt-generate-dialog">
          <DialogHeader>
            <DialogTitle>Generate Payout Statements</DialogTitle>
            <DialogDescription>Builds Draft statements for the {cycle?.label} cycle from Approved + Imported records.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Payee Type</Label>
              <Select value={genPayeeType} onValueChange={setGenPayeeType}>
                <SelectTrigger data-testid="stmt-gen-payee-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Agency">Agency</SelectItem>
                  <SelectItem value="Manager">Manager</SelectItem>
                  <SelectItem value="Agent">Agent</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="text-xs text-zinc-500">Cycle: {cycle?.cycle_start?.slice(0, 10)} → {cycle?.cycle_end?.slice(0, 10)} • Payday: {cycle?.payout_date}</div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGenOpen(false)}>Cancel</Button>
            <Button onClick={generate} disabled={generating} data-testid="stmt-gen-confirm">{generating ? 'Generating…' : 'Generate'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <SendEmailDialog
        open={!!emailTarget}
        statement={emailTarget}
        onClose={() => setEmailTarget(null)}
        onSent={load}
      />
    </div>
  );
}
